import type { RouteRecordRaw } from 'vue-router';
import { createRouter, createWebHistory, RouterView } from 'vue-router';
import { setupGuards } from './guards';

// Intermediate routes without their own layout still need a component so Vue Router
// renders child routes at the correct depth inside AuthedContent's <router-view>.
const RouterViewPassthrough = RouterView;

const guestRoutes: RouteRecordRaw = {
    path: '/',
    component: () => import('@/layouts/Guest.vue'),
    meta: { guest: true },
    children: [
        { path: '', name: 'welcome', component: () => import('@/Pages/Welcome.vue') },
        { path: 'login', name: 'login', component: () => import('@/Pages/Login.vue') },
        { path: 'register', name: 'register', component: () => import('@/Pages/Register.vue') },
        { path: 'verify-email/:token', name: 'verify-email', component: () => import('@/Pages/VerifyEmail.vue') },
        { path: 'verify-email-change/:token', name: 'verify-email-change', component: () => import('@/Pages/VerifyEmailChange.vue') },
        { path: 'terms', name: 'terms', component: () => import('@/Pages/Terms.vue') },
        { path: 'password/reset', name: 'password.reset', component: () => import('@/Pages/RequestPasswordReset.vue') },
        { path: 'password/reset/:token', name: 'password.reset.token', component: () => import('@/Pages/ResetPassword.vue') },
        { path: 'subdomain-select', name: 'subdomain-select', component: () => import('@/Pages/ServiceProviderSelect.vue') },
        { path: 'register-company', name: 'register-company', component: () => import('@/Pages/Admin/RegisterServiceProvider.vue') },
        { path: 'register-company-success/:subdomainSlug', name: 'register-company-success', component: () => import('@/Pages/Admin/RegisterServiceProviderSuccess.vue') },
        { path: 'select-account', name: 'select-account', component: () => import('@/Pages/Shared/AccountSelect.vue'), meta: { requiresAuth: true, guest: false } },
        { path: 'register-merchant', name: 'register-merchant', component: () => import('@/Pages/RegisterMerchant.vue'), meta: { requiresAuth: true, guest: false } },
    ],
};

const sharedRoutes: RouteRecordRaw[] = [
    // Billing
    { path: 'billing', redirect: 'billing/charges' },
    { path: 'billing/charges', component: () => import('@/Pages/Shared/Billing/Charges.vue'), meta: { merchantScoped: true } },
    { path: 'billing/invoices', component: () => import('@/Pages/Shared/Billing/Invoices.vue'), meta: { merchantScoped: true } },
    { path: 'billing/charges/create', component: () => import('@/Pages/Shared/Billing/ChargeCreate.vue') },
    { path: 'billing/charges/:charge', component: () => import('@/Pages/Shared/Billing/ChargeView.vue'), meta: { merchantLocked: true } },

    // Channels
    { path: 'channels', component: () => import('@/Pages/Shared/Channels/ChannelsIndex.vue'), meta: { merchantScoped: true } },
    {
        path: 'channels/create',
        component: () => import('@/Pages/Shared/Channels/ChannelCreate.vue'),
        // Walmart channels are created in MerchantHQ, so older links open the connect modal on the Channels page instead.
        beforeEnter: to => (to.query['channel-type'] === 'walmart' ? { path: to.path.replace(/\/create$/, ''), query: { connect: 'walmart' } } : true),
    },
    { path: 'channels/:channel/edit', component: () => import('@/Pages/Shared/Channels/ChannelEdit.vue'), meta: { merchantLocked: true } },
    { path: 'channels/:channel/listings', component: () => import('@/Pages/Shared/Listings/ListingsIndex.vue'), meta: { merchantLocked: true } },
    { path: 'channels/listings/:listing', component: () => import('@/Pages/Shared/Listings/ListingView.vue'), meta: { merchantLocked: true } },
    { path: 'channels/:channel/wfs/inbound-orders', component: () => import('@/Pages/Shared/Channels/Wfs/WfsInboundOrdersIndex.vue'), meta: { merchantLocked: true } },
    { path: 'channels/:channel/wfs/inbound-orders/create', component: () => import('@/Pages/Shared/Channels/Wfs/WfsInboundOrderCreate.vue'), meta: { merchantLocked: true } },
    { path: 'channels/walmart/wfs/inbound-orders/:uuid', component: () => import('@/Pages/Shared/Channels/Wfs/WfsInboundOrderView.vue'), meta: { merchantLocked: true } },

    // FBA Transport
    { path: 'fba-transport/v2024/plans/:fbaTransportV2024PlanId', component: () => import('@/Pages/Shared/FbaTransport/FbaTransportV2024Plan.vue'), meta: { merchantLocked: true } },

    // Inventory
    { path: 'inventory', component: () => import('@/Pages/Shared/Inventory/InventoryIndex.vue'), meta: { merchantScoped: true } },
    { path: 'inventory/bundle-configurations', component: () => import('@/Pages/Shared/Inventory/BundleConfigurations.vue') },
    { path: 'inventory/production', component: () => import('@/Pages/Shared/Inventory/Production.vue') },
    { path: 'inventory/:item', component: () => import('@/Pages/Shared/Inventory/InventoryView.vue'), meta: { merchantLocked: true } },

    // Orders
    { path: 'orders', component: () => import('@/Pages/Shared/Orders/OrdersIndex.vue'), meta: { merchantScoped: true } },
    { path: 'orders/create', component: () => import('@/Pages/Shared/Orders/OrderCreate.vue') },
    { path: 'orders/mapImport', component: () => import('@/Pages/Shared/Orders/OrderMapSheet.vue') },
    { path: 'orders/:order', component: () => import('@/Pages/Shared/Orders/OrderView.vue'), meta: { merchantLocked: true } },
    { path: 'orders/:order/edit', component: () => import('@/Pages/Shared/Orders/OrderEdit.vue'), meta: { merchantLocked: true } },

    // Inbound Shipments
    { path: 'shipments/inbound', component: () => import('@/Pages/Shared/Shipments/Inbound/InboundShipmentIndex.vue'), meta: { merchantScoped: true } },
    { path: 'shipments/inbound/create', component: () => import('@/Pages/Shared/Shipments/Inbound/InboundShipmentCreate.vue') },
    { path: 'shipments/inbound/import-amazon', component: () => import('@/Pages/Shared/Shipments/ImportAmazon.vue') },
    { path: 'shipments/inbound/:inboundShipment', component: () => import('@/Pages/Shared/Shipments/Inbound/InboundShipmentView.vue'), meta: { merchantLocked: true } },

    // Outbound Shipments
    { path: 'shipments/outbound', component: () => import('@/Pages/Shared/Shipments/Outbound/OutboundShipmentIndex.vue'), meta: { merchantScoped: true } },
    { path: 'shipments/outbound/create', component: () => import('@/Pages/Shared/Shipments/Outbound/OutboundShipmentCreate.vue') },
    { path: 'shipments/outbound/:outboundShipment/self-arranged-transport', component: () => import('@/Pages/Shared/Shipments/Outbound/SelfArrangeTransportView.vue'), meta: { merchantLocked: true } },
    { path: 'shipments/outbound/:outboundShipment', component: () => import('@/Pages/Shared/Shipments/Outbound/OutboundShipmentView.vue'), meta: { merchantLocked: true } },

    // Actions
    { path: 'actions/fix-items', component: () => import('@/Pages/Shared/Actions/FixIssues.vue') },
    { path: 'actions/training', component: () => import('@/Pages/Shared/Actions/Training.vue') },

    // Integrations
    { path: 'integrations', component: () => import('@/Pages/Shared/Integrations/IntegrationsDirectory.vue'), meta: { merchantScoped: true, requiresPlatformOauthManagement: true } },
];

// Every shared route is mounted twice: once under a required merchant slug so the
// merchant is always resolvable from the URL itself (staff no longer need to pre-select
// one to deep-link into a resource), and once bare so legacy un-prefixed links still
// match and can be canonicalized. A single *optional* param wrapper would be simpler,
// but it's ambiguous with a `path: ''` sibling (e.g. staff.dashboard) — vue-router's
// matcher silently prefers the optional-param route over the static empty one, so
// `/staff` would stop resolving to the staff dashboard. Shared routes carry no names
// (enforced by eslint.config.js): a name would be registered twice, and navigating to
// it by name from a page without a slug would throw on the missing required param.
const merchantScopedRoutes = (children: RouteRecordRaw[]): RouteRecordRaw[] => [
    { path: ':merchantSlug(m_[^/]+)', component: RouterViewPassthrough, children },
    ...children,
];

const scanWorkflowRoutes: RouteRecordRaw = {
    path: '/staff/handheld',
    component: () => import('@/layouts/ScanWorkflow.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
    children: [
        { path: '', name: 'staff.scanWorkflow.index', component: () => import('@/Pages/ScanWorkflow/Index.vue') },
        { path: 'putaway/inbound', name: 'staff.scanWorkflow.putaway', component: () => import('@/Pages/ScanWorkflow/FetchShipmentList.vue') },
        { path: 'putaway/inbound/:inboundShipment', name: 'staff.scanWorkflow.putaway.shipment', component: () => import('@/Pages/ScanWorkflow/Putaway/Inbound/PutawayShipmentItems.vue') },
        { path: 'receive', name: 'staff.scanWorkflow.receive-select', component: () => import('@/Pages/ScanWorkflow/ReceiveSelect.vue') },
        { path: 'receive/inbound', name: 'staff.scanWorkflow.receive', component: () => import('@/Pages/ScanWorkflow/FetchShipmentList.vue') },
        { path: 'receive/inbound/:inboundShipment', name: 'staff.scanWorkflow.receive.shipment', component: () => import('@/Pages/ScanWorkflow/Receive/Inbound/ReceiveShipmentItems.vue') },
        { path: 'fnsku-receive/inbound', name: 'staff.scanWorkflow.fnsku-receive', component: () => import('@/Pages/ScanWorkflow/FetchShipmentList.vue') },
        { path: 'fnsku-receive/inbound/:inboundShipment', name: 'staff.scanWorkflow.fnsku-receive.shipment', component: () => import('@/Pages/ScanWorkflow/FnskuReceive/FnskuReceiveShipmentItems.vue') },
        { path: 'box-contents/outbound', name: 'staff.scanWorkflow.box-contents', component: () => import('@/Pages/ScanWorkflow/FetchShipmentList.vue') },
        { path: 'box-contents/outbound/:outboundShipment', name: 'staff.scanWorkflow.box-contents.shipment', component: () => import('@/Pages/ScanWorkflow/BoxContents/Outbound/FbaBoxContentsShipmentItems.vue') },
        { path: 'settings', name: 'staff.scanWorkflow.settings', component: () => import('@/Pages/ScanWorkflow/Settings.vue') },
        { path: 'settings/warehouse', name: 'staff.scanWorkflow.settings.warehouse', component: () => import('@/Pages/ScanWorkflow/WarehouseSettings.vue') },
        { path: 'settings/station', name: 'staff.scanWorkflow.settings.station', component: () => import('@/Pages/ScanWorkflow/StationSettings.vue') },
    ],
};

const authRoutes: RouteRecordRaw = {
    path: '/',
    component: () => import('@/layouts/Content.vue'),
    meta: { requiresAuth: true },
    children: [
        // Settings
        { path: 'settings', name: 'settings', component: () => import('@/Pages/Shared/Settings/UserSettings.vue') },
        { path: 'merchant-settings', name: 'merchant-settings', component: () => import('@/Pages/Shared/Settings/MerchantSettings.vue') },

        // Dashboard. The bare route keeps the `dashboard` name because guards redirect to
        // `{ name: 'dashboard' }` from pages that carry no merchant slug, where a required
        // slug param could not be filled in; the guard forwards it to the scoped variant.
        { path: 'dashboard', name: 'dashboard', component: () => import('@/Pages/Shared/ClientDashboard.vue') },
        { path: ':merchantSlug(m_[^/]+)/dashboard', name: 'merchant.dashboard', component: () => import('@/Pages/Shared/ClientDashboard.vue') },

        // Shared routes at root level (merchant context)
        ...merchantScopedRoutes(sharedRoutes),

        // Staff routes
        {
            path: 'staff',
            component: RouterViewPassthrough,
            meta: { requiresStaff: true },
            children: [
                { path: '', name: 'staff.dashboard', component: () => import('@/Pages/Staff/Dashboard.vue') },
                { path: 'reconcile', component: () => import('@/Pages/Staff/Warehouses/Reconcile.vue') },
                { path: 'service-provider-billing', name: 'staff.service-provider-billing.overview', component: () => import('@/Pages/Staff/ServiceProviderBilling/ServiceProviderBillingPage.vue') },

                // Merchants
                { path: 'merchants', component: () => import('@/Pages/Staff/Merchants/MerchantsIndex.vue') },
                { path: 'merchants/create', component: () => import('@/Pages/Staff/Merchants/CreateMerchant.vue') },
                { path: 'merchants/:merchant', component: () => import('@/Pages/Staff/Merchants/MerchantsIndex.vue') },
                { path: 'merchants/:merchant/edit', component: () => import('@/Pages/Staff/Merchants/MerchantEdit.vue') },

                // Company Services
                { path: 'services', component: () => import('@/Pages/Staff/CompanyServices/CompanyServicesIndex.vue') },
                { path: 'services/create', component: () => import('@/Pages/Staff/CompanyServices/CompanyServicesCreate.vue') },
                { path: 'services/:service', component: () => import('@/Pages/Staff/CompanyServices/CompanyServicesIndex.vue') },
                { path: 'services/:service/edit', component: () => import('@/Pages/Staff/CompanyServices/CompanyServicesEdit.vue') },

                // Stations
                { path: 'stations/create', meta: { requiresOwner: true }, component: () => import('@/Pages/Staff/Stations/StationCreate.vue') },
                { path: 'stations/:station/edit', meta: { requiresOwner: true }, component: () => import('@/Pages/Staff/Stations/StationEdit.vue') },

                // Settings
                { path: 'notification-settings', component: () => import('@/Pages/Staff/Settings/ServiceProviderNotificationSettings.vue') },
                { path: 'settings', component: () => import('@/Pages/Staff/Settings/ServiceProviderAccountSettings.vue') },

                // Billing
                { path: 'billing/receivables', component: () => import('@/Pages/Shared/Billing/Receivables.vue') },

                // Reports
                { path: 'reports', component: () => import('@/Pages/Staff/Reports/ServiceProviderReportsIndex.vue') },

                // Orders (staff-specific)
                { path: 'orders/pick', component: () => import('@/Pages/Staff/Orders/OrdersPick.vue') },
                { path: 'orders/pack', component: () => import('@/Pages/Staff/Orders/OrdersPack.vue') },

                // Shared routes under /staff
                ...merchantScopedRoutes(sharedRoutes),

                // Warehouses (staff only)
                { path: 'warehouses', component: () => import('@/Pages/Staff/Warehouses/WarehousesIndex.vue') },
                { path: 'warehouses/create', component: () => import('@/Pages/Staff/Warehouses/WarehouseCreate.vue') },
                { path: 'warehouses/:warehouse', component: () => import('@/Pages/Staff/Warehouses/WarehouseView.vue') },
                { path: 'warehouses/:warehouse/locations', component: () => import('@/Pages/Staff/Warehouses/LocationsIndex.vue') },
                { path: 'warehouses/:warehouse/locations/:location', component: () => import('@/Pages/Staff/Warehouses/LocationView.vue') },
            ],
        },

        // Admin routes
        {
            path: 'admin',
            component: RouterViewPassthrough,
            meta: { requiresDeveloper: true },
            children: [
                { path: '', name: 'admin.dashboard', component: () => import('@/Pages/Admin/Dashboard.vue') },
                { path: 'users', component: () => import('@/Pages/Admin/Users.vue') },
                { path: 'serviceProviderBilling', component: () => import('@/Pages/Admin/ServiceProviderBilling.vue') },
                { path: 'serviceProviderEntitlements', component: () => import('@/Pages/Admin/ServiceProviderEntitlements.vue') },
                { path: 'customer-support-operations', name: 'admin.customer-support-operations', component: () => import('@/Pages/Admin/CustomerSupportOperations.vue') },
                { path: 'amazonRequests', component: () => import('@/Pages/Admin/AmazonRequests.vue') },
                { path: 'preferences', component: () => import('@/Pages/Admin/Preferences.vue') },
                {
                    path: 'partner-apps',
                    component: () => import('@/Pages/Admin/PartnerAppsSection.vue'),
                    meta: { requiresPlatformOauthManagement: true },
                    children: [
                        { path: '', name: 'admin.partner-apps', component: () => import('@/Pages/Admin/PartnerApps.vue') },
                        { path: 'partner-access', name: 'admin.partner-apps.partner-access', component: () => import('@/Pages/Admin/PartnerAccess.vue') },
                        { path: ':partnerAppId', name: 'admin.partner-apps.show', component: () => import('@/Pages/Admin/PartnerAppView.vue') },
                    ],
                },
            ],
        },
    ],
};

const partnerRoutes: RouteRecordRaw = {
    path: '/partners',
    component: () => import('@/layouts/Partner.vue'),
    meta: { requiresPartner: true },
    children: [
        { path: '', redirect: { name: 'partners.apps' } },
        { path: 'apps', name: 'partners.apps', component: () => import('@/Pages/Partners/PartnerApps.vue') },
    ],
};

const merchantEntitlementAdminRoutes: RouteRecordRaw = {
    path: '/admin/merchant-entitlements',
    component: () => import('@/layouts/Admin.vue'),
    meta: { requiresDeveloper: true },
    children: [
        { path: '', name: 'admin.merchant-entitlements', component: () => import('@/Pages/Admin/MerchantEntitlements.vue') },
    ],
};

const emptyRoutes: RouteRecordRaw = {
    path: '/',
    component: () => import('@/layouts/Empty.vue'),
    children: [
        { path: 'error', name: 'error', component: () => import('@/Pages/ErrorPage.vue') },
        // Deliberately outside requiresAuth: consent must not depend on a selected merchant or
        // the select-account redirect, and the consent endpoints enforce authentication
        // themselves. Its URL carries the pending authorization request ID, so no pageview.
        { path: 'connect/authorize', name: 'platform-oauth.authorize', component: () => import('@/Pages/Shared/PlatformOauthAuthorize.vue'), meta: { trackPageview: false } },
    ],
};

const routes: RouteRecordRaw[] = [
    guestRoutes,
    scanWorkflowRoutes,
    authRoutes,
    merchantEntitlementAdminRoutes,
    partnerRoutes,
    emptyRoutes,

    // Catch-all redirect to error page
    { path: '/:pathMatch(.*)*', redirect: { name: 'error', query: { status: '404' } } },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

setupGuards(router);

// When a lazy-loaded route chunk fails (e.g. after a deploy replaced the file),
// reload directly to the intended destination so the user lands on the new route.
// A sessionStorage flag prevents an infinite reload loop when the failure is
// persistent (broken deploy, upstream cache serving stale HTML, etc.).
const CHUNK_RELOAD_KEY = 'vite_chunk_reload_attempted';

router.onError((error, to) => {
    const isChunkError = (
        error?.message?.includes('Failed to fetch dynamically imported module') === true // Chrome
        || error?.message?.includes('Importing a module script failed') === true // Safari
        || error?.message?.includes('error loading dynamically imported module') === true // Firefox
        || error?.message?.includes('Couldn\'t resolve component') === true // Vue Router: module loaded but default export was falsy
    );

    if (!isChunkError) return;

    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) != null) {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        router.push({ name: 'error', query: { status: '503' } });
        return;
    }

    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    window.location.href = to.fullPath;
});

// Clear the retry flag after every successful navigation so a chunk error on a
// later route still gets its one reload attempt.
router.afterEach(() => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
});

export default router;
